import React, {createContext, useContext, useEffect} from 'react'
import {useAppSelector, useAppDispatch} from '../../../setup/redux/hooks'
import {
  getNews,
  dismissAnnouncement,
  showAnnouncement,
  addAnnouncement,
  selectCurrentAnnouncement,
  selectAnnouncementLoading,
  selectAnnouncementError,
  selectAnnouncements,
} from '../../../app/shared/redux/AnnouncementRedux'
import {AnnouncementModel} from '../../../app/shared/models/AnnouncementModel'

interface AnnouncementContextType {
  currentAnnouncement: AnnouncementModel | null
  announcements: AnnouncementModel[]
  isLoading: boolean
  error: string | null
  refreshNews: () => void
  dismissAnnouncement: (id: string, permanent?: boolean) => void
  showAnnouncement: (id: string) => void
  addAnnouncement: (announcement: AnnouncementModel) => void
}

const AnnouncementContext = createContext<AnnouncementContextType | null>(null)

export const useAnnouncement = () => {
  const context = useContext(AnnouncementContext)
  if (!context) {
    throw new Error('useAnnouncement must be used within AnnouncementProvider')
  }
  return context
}

export const AnnouncementProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const dispatch = useAppDispatch()
  const currentAnnouncement = useAppSelector(selectCurrentAnnouncement)
  const announcements = useAppSelector(selectAnnouncements)
  const isLoading = useAppSelector(selectAnnouncementLoading)
  const error = useAppSelector(selectAnnouncementError)

  // Fetch news on mount
  useEffect(() => {
    dispatch(getNews())
  }, [dispatch])

  const refreshNews = () => {
    dispatch(getNews())
  }

  const handleDismissAnnouncement = (id: string, permanent = true) => {
    dispatch(dismissAnnouncement({id, permanent}))
  }

  const handleShowAnnouncement = (id: string) => {
    dispatch(showAnnouncement(id))
  }

  const handleAddAnnouncement = (announcement: AnnouncementModel) => {
    dispatch(addAnnouncement(announcement))
  }

  const value: AnnouncementContextType = {
    currentAnnouncement,
    announcements,
    isLoading,
    error,
    refreshNews,
    dismissAnnouncement: handleDismissAnnouncement,
    showAnnouncement: handleShowAnnouncement,
    addAnnouncement: handleAddAnnouncement,
  }

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>
}
